// §11.7 — Photos: zoom/pan, folder navigation, slideshow, Share, Set as wallpaper, and Edit.
//
// **Edit is a `share`, not a feature** (D-018): "Hangar has no built-in raster editor", so the
// Edit action hands the image to whatever registered editor the user has — PixelSmith when it is
// installed, nothing when it is not, and the shell's own picker says so. An app that grew its own
// crop tool would be the beginning of a second PixelSmith.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  basename,
  extname,
  type FileEntry,
  type HangarAPI,
  type PhotosShellAccess,
  type VfsPath,
} from '@hangar/shared';
import { COPY } from '@/copy';
import { PHOTOS_SLIDESHOW_MS, PHOTOS_ZOOM_MAX, PHOTOS_ZOOM_MIN } from '@/config';

/** §11.7 — what Photos will open. The registry claims these; this is the same list. */
export const PHOTO_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.avif', '.bmp'] as const;

const MIME: Record<string, string> = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.avif': 'image/avif',
  '.bmp': 'image/bmp',
};

const isPhoto = (name: string): boolean =>
  (PHOTO_EXTENSIONS as readonly string[]).includes(extname(name).toLowerCase());

interface Pan {
  readonly x: number;
  readonly y: number;
}

export function Photos({ api, access }: { api: HangarAPI; access: PhotosShellAccess }) {
  const [path, setPath] = useState<VfsPath | null>(null);
  /** §11.7's "folder navigation": every image beside the one that was opened. */
  const [siblings, setSiblings] = useState<readonly FileEntry[]>([]);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState<Pan>({ x: 0, y: 0 });
  const [slideshow, setSlideshow] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const dragging = useRef<{ x: number; y: number } | null>(null);

  // §9.5 — the path this window was launched to open. The *only* way an app learns it (D-080).
  useEffect(() => {
    void api.launch.openedPath().then((opened) => {
      if (opened) setPath(opened);
    });
  }, [api]);

  // §11.7's folder navigation. Through the capability rather than `api.fs`, because §9.5 grants
  // an app the file the user opened and not its folder — the widening is one directory, and it is
  // the shell that performs the listing (D-169).
  useEffect(() => {
    if (!path) return;
    void access
      .siblings(path)
      .then((entries) => setSiblings(entries.filter((entry) => isPhoto(entry.name))))
      .catch(() => setSiblings([]));
  }, [access, path]);

  // The bytes. Revoked on the way out — an object URL per image, held forever, is a memory leak
  // with a picture attached.
  useEffect(() => {
    if (!path) return;
    let cancelled = false;
    let url: string | null = null;

    void api.fs
      .read(path)
      .then((bytes) => {
        if (cancelled) return;
        url = URL.createObjectURL(
          new Blob([bytes as BlobPart], { type: MIME[extname(path).toLowerCase()] ?? 'image/png' }),
        );
        setObjectUrl(url);
        setZoom(1);
        setPan({ x: 0, y: 0 });
      })
      .catch(() => {
        if (!cancelled) setMessage(COPY.photos.unreadable);
      });

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [api, path]);

  const index = useMemo(
    () => (path ? siblings.findIndex((entry) => entry.path === path) : -1),
    [path, siblings],
  );

  const stepTo = useCallback(
    (delta: number) => {
      if (siblings.length === 0) return;
      const next = siblings[(index + delta + siblings.length) % siblings.length];
      if (next) setPath(next.path);
    },
    [index, siblings],
  );

  // §11.7's slideshow. One timer, restarted whenever the picture changes, so a manual Next while
  // it is running resets the clock rather than leaving a short first slide behind it.
  useEffect(() => {
    if (!slideshow || siblings.length < 2) return;
    const timer = setTimeout(() => stepTo(1), PHOTOS_SLIDESHOW_MS);
    return () => clearTimeout(timer);
  }, [slideshow, siblings.length, path, stepTo]);

  const share = useCallback(
    async (note: string) => {
      if (!path) return;
      const bytes = await api.fs.read(path);
      const result = await api.transfer.share({
        items: [
          { mime: MIME[extname(path).toLowerCase()] ?? 'image/png', bytes, path },
        ],
      });
      setMessage(result === 'delivered' ? note : COPY.photos.shareCancelled);
    },
    [api, path],
  );

  if (!path) {
    return (
      <div className="photos is-empty" data-testid="photos">
        <p data-testid="photos-empty">{COPY.photos.empty}</p>
      </div>
    );
  }

  return (
    <div className="photos" data-testid="photos">
      <div className="photos-bar">
        <button
          type="button"
          data-testid="photos-prev"
          aria-label={COPY.photos.previous}
          disabled={siblings.length < 2}
          onClick={() => stepTo(-1)}
        >
          ‹
        </button>
        <span className="photos-name" data-testid="photos-name">
          {basename(path)}
        </span>
        <button
          type="button"
          data-testid="photos-next"
          aria-label={COPY.photos.next}
          disabled={siblings.length < 2}
          onClick={() => stepTo(1)}
        >
          ›
        </button>

        <span className="photos-spacer" />

        <button
          type="button"
          data-testid="photos-zoom-out"
          aria-label={COPY.photos.zoomOut}
          onClick={() => setZoom((current) => Math.max(PHOTOS_ZOOM_MIN, current / 1.25))}
        >
          −
        </button>
        <span className="photos-zoom" data-testid="photos-zoom">{`${Math.round(zoom * 100)}%`}</span>
        <button
          type="button"
          data-testid="photos-zoom-in"
          aria-label={COPY.photos.zoomIn}
          onClick={() => setZoom((current) => Math.min(PHOTOS_ZOOM_MAX, current * 1.25))}
        >
          +
        </button>
        <button
          type="button"
          data-testid="photos-zoom-reset"
          onClick={() => {
            setZoom(1);
            setPan({ x: 0, y: 0 });
          }}
        >
          {COPY.photos.fit}
        </button>

        <button
          type="button"
          data-testid="photos-slideshow"
          data-on={String(slideshow)}
          disabled={siblings.length < 2}
          onClick={() => setSlideshow((on) => !on)}
        >
          {slideshow ? COPY.photos.slideshowStop : COPY.photos.slideshow}
        </button>

        <button
          type="button"
          data-testid="photos-wallpaper"
          onClick={() => {
            access.setWallpaper(path);
            setMessage(COPY.photos.wallpaperSet);
          }}
        >
          {COPY.photos.setWallpaper}
        </button>

        <button
          type="button"
          data-testid="photos-share"
          onClick={() => void share(COPY.photos.shared)}
        >
          {COPY.photos.share}
        </button>

        {/* D-018 — Edit is a share to a registered editor. Hangar has no raster editor. */}
        <button
          type="button"
          data-testid="photos-edit"
          onClick={() => void share(COPY.photos.sentToEditor)}
        >
          {COPY.photos.edit}
        </button>
      </div>

      <div
        className="photos-stage"
        data-testid="photos-stage"
        onPointerDown={(event) => {
          dragging.current = { x: event.clientX - pan.x, y: event.clientY - pan.y };
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          const from = dragging.current;
          if (!from) return;
          setPan({ x: event.clientX - from.x, y: event.clientY - from.y });
        }}
        onPointerUp={(event) => {
          dragging.current = null;
          event.currentTarget.releasePointerCapture(event.pointerId);
        }}
        onWheel={(event) => {
          if (!event.ctrlKey && !event.metaKey) return;
          event.preventDefault();
          setZoom((current) =>
            Math.min(PHOTOS_ZOOM_MAX, Math.max(PHOTOS_ZOOM_MIN, current * (event.deltaY < 0 ? 1.1 : 0.9))),
          );
        }}
      >
        {objectUrl && (
          <img
            className="photos-image"
            data-testid="photos-image"
            src={objectUrl}
            alt={basename(path)}
            draggable={false}
            style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
          />
        )}
      </div>

      {message && (
        <div className="photos-message" role="status" data-testid="photos-message">
          {message}
        </div>
      )}
    </div>
  );
}
